/**
 * Per-tenant user management API.
 * Portfolio admins see/manage all users; tenant admins only their own businesses.
 * Seats: SEAT_LIMIT per tenant (default 5) — basis for future per-seat billing.
 */
import { API_BASE, apiFetch, SessionUser } from './base';

export interface ManagedUser extends SessionUser {}

async function handle(res: Response): Promise<any> {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `Request failed (${res.status})`);
  return data;
}

export const usersApi = {
  async list(portcoId?: string): Promise<{ users: ManagedUser[]; seatLimit: number }> {
    const q = portcoId && portcoId !== 'all' ? `?portcoId=${encodeURIComponent(portcoId)}` : '';
    const res = await apiFetch(`${API_BASE}/auth/users${q}`);
    return handle(res);
  },

  async create(body: { name: string; email: string; password: string; role: string; tenants: string[] }): Promise<ManagedUser> {
    const res = await apiFetch(`${API_BASE}/auth/users`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return (await handle(res)).user;
  },

  async update(userId: string, body: Partial<{ name: string; password: string; role: string; tenants: string[] }>): Promise<ManagedUser> {
    const res = await apiFetch(`${API_BASE}/auth/users/${encodeURIComponent(userId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return (await handle(res)).user;
  },

  async remove(userId: string): Promise<void> {
    const res = await apiFetch(`${API_BASE}/auth/users/${encodeURIComponent(userId)}`, { method: 'DELETE' });
    await handle(res);
  },
};
